import { FC, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';

import { RootState } from 'types';
import { dispatch } from 'store';
import { getDashboardInfo } from 'modules/dashboard/actions';

import { Container, HeaderSecond } from 'views/components';

import styles from './Dashboard.module.css';
import { goToWP, t } from 'utils';

import texts from './texts';
import {getClickCountTotal} from "../../../modules/user/actions";
import {Api} from "../../../services/api";
import { routes } from 'routes';

export const Dashboard: FC = () => {
  // store
  const { account_info, plan, billing_history, machines } = useSelector(
    (state: RootState) => state.dashboard,
  );
  let location = useLocation();
  const apiToken = !!Api.getAxios().defaults.headers.Authorization;

  const { currencyType, totalClick } = useSelector((state: RootState) => state.user);

  let ownerPlan = machines.find((o: { owner: null }) => o.owner !== null);
  const enterprisePlan = plan.price === 'Enterprise';
  const subclinicPlan = enterprisePlan && !ownerPlan;

  const onFocus = () => {
    dispatch(getDashboardInfo.request());
  }

  useEffect(() => {
    if(location.pathname === routes.dashboard) {
      window.addEventListener("focus", onFocus);
      return () => {
          window.removeEventListener("focus", onFocus);
      };
    }
  }, [location]);

  useEffect(() => {
    dispatch(getDashboardInfo.request());
    dispatch(getClickCountTotal.request());
  }, [apiToken]);

  return (
    <Container>
      <HeaderSecond />
      <p className={styles.fullName}>
        {t(texts.dashboard.hi)}, {account_info.full_name}
      </p>
      <div className={styles.content}>
        <div className={styles.main}>
          <div>
            <div className={styles.currentTop}>
              <p className={styles.bold}>{t(texts.dashboard.yourCurrentPlan)}</p>
              <Link className={styles.edit} to="/licence">
                {t(texts.dashboard.edit)}
              </Link>
            </div>
            <div className={styles.currentPlan}>
              <div>
                {plan.name && !plan.is_active ? (
                  <div className={styles.planWrap}>
                    <p style={{ textTransform: 'capitalize' }}>{plan.name}</p>
                    <div
                      style={{
                        backgroundColor: '#F6595F',
                        padding: '10px',
                        borderRadius: 5,
                        color: '#fff',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {t(texts.dashboard.expired)}
                    </div>
                  </div>
                ) : (
                  <div className={styles.planWrap}>
                    <p style={{ textTransform: 'capitalize' }}>{plan.name}</p>
                    <p className={styles.bold}>
                      {plan.price === 'Trial'
                        ? plan.price
                        : plan.price === 'Enterprise'
                        ? plan.price
                        : plan.name &&
                          `${
                            plan.currency
                              ? plan.currency.toLocaleUpperCase()
                              : currencyType && currencyType.toUpperCase()
                          } ${plan.price}`}
                    </p>
                  </div>
                )}
              </div>
              {plan.start_date && plan.expiry_date && (
                <p>
                  {t(texts.dashboard.startedfrom)}{' '}
                  {`${plan.start_date.split('-')[2]}/${plan.start_date.split('-')[1]}/${
                    plan.start_date.split('-')[0]
                  }`}
                  , {t(texts.dashboard.nextBillableDate)}:{' '}
                  {`${plan.expiry_date.split('-')[2]}/${plan.expiry_date.split('-')[1]}/${
                    plan.expiry_date.split('-')[0]
                  }`}
                </p>
              )}
              <div style={{ height: '1px', backgroundColor: '#D0D1D3' }} />
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <p style={{ marginRight: '30px' }}>
                  {t(texts.dashboard.youHave)}{' '}
                  {plan.number_of_machines ? plan.number_of_machines : ''}{' '}
                  {t(texts.dashboard.licences)}
                </p>
                <Link className={styles.link} to="/licence">
                  {t(texts.dashboard.manageLicences)}
                </Link>
              </div>
            </div>
          </div>
          {!subclinicPlan && (
            <div>
              <div className={styles.currentTop}>
                <p
                  style={{ fontWeight: 'bold', color: 'rgba(38, 38, 38, 0.8)', marginTop: '30px' }}
                >
                  {t(texts.dashboard.billingHistory)}
                </p>
              </div>
              {billing_history.length > 0 ? (
                <div
                  style={{
                    backgroundColor: '#fff',
                    border: '1px solid #d0d1d3',
                    padding: '20px 0',
                  }}
                >
                  {billing_history.map((el: any, i: number) => (
                    <>
                      <div className={styles.currentPlanBlock} key={el.url || i}>
                        {i !== 0 && <div style={{ height: 1, backgroundColor: '#D0D1D3' }} />}
                        <div className={`${styles.billingItem}`}>
                            <p className={styles.billingDate}>{`${el.created_at?.split('-')[2]}/${
                              el.created_at?.split('-')[1]
                            }/${el.created_at?.split('-')[0]}`}</p>

                            <p style={{ fontSize: '15px', margin: 0, padding: '0 10px' }}>{el.name}</p>
                            <p style={{ fontSize: '15px', margin: 0 }}>{el.refund || '-'}</p>
                            <p
                              style={{
                                color: 'rgba(38, 38, 38, 0.8)',
                                fontWeight: 'bold',
                                fontSize: '13px',
                                margin: 0,
                              }}
                            >
                              {el.price === 'Enterprise'
                                ? el.price
                                : `${el.currency.toUpperCase()} ${el.price}`}
                            </p>
                            <a className={styles.link} href={el.url} target="_blank">
                              {t(texts.dashboard.receipt)}
                            </a>
                        </div>
                      </div>
                    </>
                  ))}
                </div>
              ) : (
                <div>
                  <p>{t(texts.dashboard.youHaveNoBillingHistory)}</p>
                </div>
              )}
            </div>
          )}
        </div>
        <div className={styles.sidebar}>
          <div className={styles.currentTop}>
            <p className={styles.bold}>{t(texts.dashboard.accountInformation)}</p>
            <Link className={styles.edit} to="/profile">
              {t(texts.dashboard.edit)}
            </Link>
          </div>
          <div className={styles.currentPlan}>
            <div className={styles.infoWrap}>
              <div className={styles.label}>{t(texts.dashboard.name)}</div>
              <div className={styles.info}>{account_info.full_name}</div>
            </div>
            <div className={styles.infoWrap}>
              <div className={styles.label}>{t(texts.dashboard.email)}</div>
              <div className={styles.info}>{account_info.email}</div>
            </div>
            <div className={styles.infoWrap}>
              <div className={styles.label}>{t(texts.dashboard.phone)}</div>
              <div className={styles.info}>{account_info.phone_number}</div>
            </div>
            <div className={styles.infoWrap}>
              <div className={styles.label}>{t(texts.dashboard.clinicName)}</div>
              <div className={styles.info}>{account_info.clinic_name}</div>
            </div>
            <div className={styles.infoWrap}>
              <div className={styles.label}>{t(texts.dashboard.yourPosition)}</div>
              <div className={styles.info}>{account_info.position_title}</div>
            </div>
          </div>

          <div className={styles.passwordBlock}>
            <p className={styles.bold}>{t(texts.dashboard.passwordDetails)}</p>
            <Link className={styles.link} to="/reset-password">
              {t(texts.dashboard.change)}
            </Link>
          </div>

          <div className={styles.passwordBlock}>
            <p className={styles.bold}>{t(texts.dashboard.creditCardDetails)}</p>
            <Link className={styles.link} to="/credit-card-details">
              {t(texts.dashboard.change)}
            </Link>
          </div>

          {/* <div className={styles.passwordBlock}>
            <p className={styles.bold}>{t(texts.dashboard.clickCounter)}</p>
            <p className={styles.link} style={{  textDecoration: 'none' }}>
              {totalClick}
            </p>
          </div> */}

          <p className={styles.bold}>{t(texts.dashboard.contactUs)}</p>
          <p style={{ color: 'rgba(38, 38, 38, 0.8)', fontSize: '14px', marginTop: '-10px' }}>
            {t(texts.dashboard.pleaseClick)}{' '}
            <a className={styles.link} onClick={() => goToWP('contact')}>
              {t(texts.dashboard.here)}
            </a>{' '}
            {t(texts.dashboard.help)}
          </p>
        </div>
      </div>
    </Container>
  );
};
